
import Vue from 'vue'
import {OfflineDownloadRecord} from '@/services/offline-library.service'
import {bookThumbnailUrl} from '@/functions/urls'
import {getBookReadRouteFromMedia} from '@/functions/book-format'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'

interface DownloadGroup {
  seriesId: string
  title: string
  downloads: OfflineDownloadRecord[]
  downloaded: number
  failed: number
  bytes: number
}

export default Vue.extend({
  name: 'OfflineDownloads',
  components: {ConfirmationDialog},
  data: () => ({
    changingMode: false,
    notificationPermission: 'Notification' in window ? Notification.permission : 'unsupported',
    confirmingRemoval: false,
    pendingRemoval: [] as OfflineDownloadRecord[],
    storage: {usage: 0, quota: 0},
  }),
  computed: {
    notificationsEnabled(): boolean {
      return this.$offline.state.preferences.notifyWhenComplete && this.notificationPermission === 'granted'
    },
    notificationHint(): string {
      if (this.notificationPermission === 'unsupported') return this.$t('offline.notification_unsupported').toString()
      if (this.notificationPermission === 'denied') return this.$t('offline.notification_denied').toString()
      return this.$t(this.notificationPermission === 'granted' ? 'offline.notification_enabled' : 'offline.notification_enable_hint').toString()
    },
    downloads(): OfflineDownloadRecord[] {
      return this.$offline.state.downloads
    },
    downloadGroups(): DownloadGroup[] {
      const grouped = new Map<string, OfflineDownloadRecord[]>()
      this.downloads.forEach(item => grouped.set(item.book.seriesId, [...(grouped.get(item.book.seriesId) || []), item]))
      return Array.from(grouped.entries()).map(([seriesId, downloads]) => ({
        seriesId,
        title: downloads[0].book.seriesTitle,
        downloads: downloads.sort((a, b) => (a.book.metadata.numberSort || 0) - (b.book.metadata.numberSort || 0)),
        downloaded: downloads.filter(item => item.status === 'downloaded').length,
        failed: downloads.filter(item => item.status === 'error' || !!item.error).length,
        bytes: downloads.reduce((sum, item) => sum + item.bytes, 0),
      }))
    },
    removalText(): string {
      if (this.pendingRemoval.length === 0) return ''
      if (this.pendingRemoval.length === 1) return this.$t('offline.confirm_remove_book', {title: this.pendingRemoval[0].book.metadata.title}).toString()
      return this.$t('offline.confirm_remove_series', {title: this.pendingRemoval[0].book.seriesTitle, count: this.pendingRemoval.length}).toString()
    },
    storagePercent(): number {
      if (!this.storage.quota) return 0
      return Math.min(100, this.storage.usage / this.storage.quota * 100)
    },
    storageHeadroom(): number {
      return Math.max(0, this.storage.quota - this.storage.usage)
    },
    storageText(): string {
      if (!this.storage.quota) return this.$t('offline.storage_unavailable').toString()
      return this.$t('offline.storage_used', {usage: this.formatBytes(this.storage.usage)}).toString()
    },
  },
  async created() {
    await this.$offline.whenReady()
    await this.refreshStorage()
  },
  methods: {
    thumbnail(bookId: string): string {
      return bookThumbnailUrl(bookId)
    },
    formatBytes(bytes: number): string {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
      const value = bytes / Math.pow(1024, index)
      return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`
    },
    async refreshStorage() {
      this.storage = await this.$offline.storageEstimate()
    },
    async changeOfflineMode(enabled: boolean) {
      this.changingMode = true
      try {
        await this.$offline.setOfflineMode(enabled)
      } finally {
        this.changingMode = false
      }
    },
    requestRemove(download: OfflineDownloadRecord) {
      this.pendingRemoval = [download]
      this.confirmingRemoval = true
    },
    requestRemoveGroup(group: DownloadGroup) {
      this.pendingRemoval = [...group.downloads]
      this.confirmingRemoval = true
    },
    async confirmRemove() {
      const removals = [...this.pendingRemoval]
      this.pendingRemoval = []
      await Promise.all(removals.map(item => this.$offline.removeDownload(item.bookId)))
      await this.refreshStorage()
    },
    async retry(bookId: string) {
      await this.$offline.downloadBook(bookId)
      await this.refreshStorage()
    },
    async retryGroupFailures(group: DownloadGroup) {
      const failed = group.downloads.filter(item => item.status === 'error' || !!item.error).map(item => item.book)
      await this.$offline.downloadBooks(failed, group.seriesId)
      await this.refreshStorage()
    },
    async pause(bookId: string) {
      await this.$offline.pauseDownload(bookId)
    },
    async resume(bookId: string) {
      await this.$offline.resumeDownload(bookId)
    },
    async setPreference(key: string, value: any) {
      await this.$offline.setDownloadPreferences({[key]: value})
    },
    async toggleNotifications(enabled: boolean) {
      if (enabled) {
        if (!('Notification' in window)) enabled = false
        else if (Notification.permission !== 'granted') enabled = (await Notification.requestPermission()) === 'granted'
      }
      await this.setPreference('notifyWhenComplete', enabled)
      this.notificationPermission = 'Notification' in window ? Notification.permission : 'unsupported'
    },
    read(download: OfflineDownloadRecord) {
      this.$router.push({
        name: getBookReadRouteFromMedia(download.book.media),
        params: {bookId: download.bookId},
      })
    },
  },
})
